import { useEffect, useMemo } from 'react';

import Store from 'helpers/store';
import Cache, { cacheKey } from 'helpers/cache';

import useStore from 'hooks/useStore';
import ReportService from '../services/reports';

const reportService = new ReportService();

const reportsCache = new Cache();

const useIndividualsReportStats = (params = {}, options = {}) => {
  const { autoFetch = true, cacheTtlMs = 60000 } = options;

  const store = useMemo(() => {
    return new Store({
      data: null,
      isLoading: !!autoFetch,
      error: null,
      activeKey: null,
    }, 'individual-report-stats');
  }, [autoFetch]);

  const actions = useMemo(() => {
    return createActions(store, { cacheTtlMs });
  }, [store, cacheTtlMs]);

  const key = useMemo(() => `individual-report-stats:${cacheKey(params || {})}`, [params]);

  useEffect(() => {
    if (autoFetch) {
      const t = setTimeout(() => {
        actions.get(params);
      });
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFetch, key]);

  useStore(store);

  return {
    data: store.get().data,
    isLoading: store.get().isLoading,
    error: store.get().error,
    refetch: () => actions.get(params),
    store,
  };
};

const createActions = (store, { cacheTtlMs }) => {
  return {
    async get(params) {
      return Promise.resolve()
        .then(() => {
          const key = `individual-report-stats:${cacheKey(params || {})}`;

          store.set(() => ({ isLoading: true, activeKey: key }));

          const cached = reportsCache.get(key);
          if (cached) {
            if (store.get().activeKey === key) {
              store.set(() => ({ data: cached, isLoading: false }));
            }
          }

          return reportService.getIndividualStats(params)
            .then(data => {
              if (data) {
                if (store.get().activeKey === key) {
                  reportsCache.set(key, data, cacheTtlMs);
                  store.set(() => ({ data }));
                }
              }
              return data;
            })
            .catch(err => {
              if (store.get().activeKey === key) {
                store.set(() => ({ error: err?.message || 'Failed to load report stats' }));
              }
            })
            .finally(() => {
              if (store.get().activeKey === key) {
                store.set(() => ({ isLoading: false }));
              }
            });
        });
    },
  };
};

export default useIndividualsReportStats; 